<script setup lang="ts">
import { ref, computed, type PropType } from 'vue';
import Popover from 'primevue/popover';
import TopBarIconButton from './TopBarIconButton.vue';
import AppTile from './AppTile.vue';
import MobileAppQrDialog from './MobileAppQrDialog.vue';
import { useTopBarPopover } from './useTopBarPopover';
import type { MobileAppModel } from '@/utils';

const props = defineProps({
  apps: {
    type: Array as PropType<MobileAppModel[]>,
    required: true
  },
  /** Product the apps belong to — titles the menu and the QR dialog. */
  productName: {
    type: String,
    required: true
  },
  /**
   * Small mark pinned to the corner of the top bar icon. Both mobile menus use the same phone
   * glyph, so a badge is what tells them apart when a study offers more than one app.
   */
  badgeLogo: {
    type: String,
    default: ''
  }
});

const { popover, toggle, hide, alignToTrigger } = useTopBarPopover();

const selectedApp = ref<MobileAppModel | null>(null);
const dialogVisible = ref(false);

const menuLabel = computed(() => `${props.productName} mobile app`);

const openApp = (app: MobileAppModel) => {
  selectedApp.value = app;
  dialogVisible.value = true;
  // The dialog takes over from here; leaving the popover open behind the mask looks broken.
  hide();
};
</script>

<template>
  <TopBarIconButton icon="mobile" :label="menuLabel" @click="toggle">
    <template v-if="badgeLogo" #badge>
      <span class="mobile-apps__badge">
        <img :src="badgeLogo" alt="" />
      </span>
    </template>
  </TopBarIconButton>

  <Popover ref="popover" @show="alignToTrigger">
    <div class="mobile-apps">
      <p class="mobile-apps__heading">Get the {{ productName }} app</p>
      <div class="mobile-apps__grid">
        <AppTile v-for="app in apps"
          :key="app.id"
          :label="app.name"
          :icon="['fab', app.icon]"
          @click="openApp(app)" />
      </div>
    </div>
  </Popover>

  <MobileAppQrDialog v-model:visible="dialogVisible"
    :app="selectedApp"
    :product-name="productName" />
</template>

<style scoped lang="scss">
.mobile-apps__heading {
  margin: 0 0 8px;
  padding: 0 4px;
  color: hsl(210, 9%, 45%);
  font-size: 12px;
  line-height: 16px;
}
.mobile-apps__grid {
  display: grid;
  grid-auto-flow: column;
  gap: 4px;
}
// The logo is a wide wordmark, so the badge is a rounded chip rather than a circle.
.mobile-apps__badge {
  position: absolute;
  right: 1px;
  bottom: 1px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 17px;
  height: 13px;
  padding: 1px 2px;
  border: 1px solid hsl(206, 25%, 92%);
  border-radius: 4px;
  background-color: white;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
}
</style>
